/**
 * Navigation of the public booking page.
 *
 * Two modes, chosen by the server (Config nav_mode, see lib/PublicBookingUrl.php) and checked here:
 *  - path (default for the top-level page): /v2/book/service/5/ via history.pushState;
 *  - hash (widgets, framed pages, PWA, kill switch): /v2/#book/service/5 as before.
 * Both modes accept both URL forms, so every link ever sent keeps working:
 *  - path mode upgrades a legacy hash (/v2/#x, /v2/?q#x, /v2/<path>#x) to a path with replaceState,
 *    a route-matching hash wins over the path; '#route' links/assignments keep routing (hashchange bridge
 *    + click handler); rendered href="#route" links are rewritten to path urls;
 *  - hash mode moves a route from the path into the hash before start and routes '/v2/<route>' links in place.
 *
 * Backbone.history is patched on the instance and only in path mode; the shared lib is also bundled into
 * the company admin (management) app, which must stay untouched.
 *
 * See docs/features/public_navigation.md
 */
define('lib/Navigation',['require','underscore','jquery','backbone','marionette','Config'],function (require) {

    var _ = require('underscore');
    var $ = require('jquery');
    var Backbone = require('backbone');
    var Marionette = require('marionette');
    var Config = require('Config');

    var MODE_PATH = 'path';
    var MODE_HASH = 'hash';

    var MAIN_ROOT = '/v2/';
    var CATCH_ALL_ROUTES = ['*notFound'];

    // OAuth / LINE LIFF responses in the fragment are not routes and must never be copied into the path
    var TOKEN_FRAGMENT = /^(access_token|context_token|id_token|code|state|error)=/;

    var Navigation = {

        _mode: null,
        _root: MAIN_ROOT,
        _stickyQuery: '',
        _stickyParams: null,
        _started: false,
        _booting: false,
        _catchAllSources: null,

        /**
         * @return {String} 'path' or 'hash'
         */
        getMode: function () {
            this._init();
            return this._mode;
        },

        isPathMode: function () {
            return this.getMode() === MODE_PATH;
        },

        /**
         * @return {String} SPA root with leading and trailing slash, e.g. '/v2/'
         */
        getRoot: function () {
            this._init();
            return this._root;
        },

        /**
         * Starts Backbone.history (replaces Backbone.history.start()). Safe to call more than once.
         *
         * @return {Boolean}
         */
        start: function () {
            if (Backbone.History.started) {
                return false;
            }
            this._init();

            var history = Backbone.history;
            this._patchDecode(history);
            this._bindClicks();
            this._bindRouteListeners();
            this._started = true;

            this._booting = true;
            try {
                return this._mode === MODE_PATH ? this._startPath(history) : this._startHash(history);
            } finally {
                this._booting = false;
            }
        },

        /**
         * Navigates to a route given in any form: 'book/service/5', '#book/service/5', '#/book', '/book',
         * '/v2/book/5/', '/v2/#book/5' or an absolute url of this site. Other http(s) urls, server pages under /v2
         * and links whose query must reach the server get a full page load; other schemes are ignored.
         *
         * @param {String} target
         * @param {Object} [options] {replace: Boolean, routeOnly: Boolean - never leave the app (values from
         *                           other windows, e.g. the widget 'navigate' message)}
         */
        go: function (target, options) {
            options = options || {};
            var value = String(target === null || target === undefined ? '' : target).replace(/^\s+|\s+$/g, '');
            var scheme = value.match(/^([a-z][a-z0-9+.\-]*):/i);
            if (scheme && !/^https?$/i.test(scheme[1])) {
                return; // javascript:, data:, ... are never followed
            }
            var fragment = this.toFragment(value);
            var query = this._queryOf(value);
            if (fragment === null && value.charAt(0) === '/' && value.charAt(1) !== '/') {
                // '/book': Backbone used to strip one leading slash, keep that for callers (e.g. widget.navigate())
                var candidate = this.clean(value);
                if (this.matchesRoute(candidate)) {
                    fragment = candidate;
                }
            }
            var leaves = fragment === null || (fragment !== '' && !this.matchesRoute(fragment))
                || this._needsServer(query);
            if (this._isUrl(value) && leaves) {
                if (!options.routeOnly) {
                    window.location.href = value; // other site, server page under /v2 (download, print, ...) or entry
                    return;
                }
                fragment = this.clean(value);
                query = '';
            }
            if (fragment === null) {
                fragment = this.clean(value);
            }
            Backbone.history.navigate(fragment + (query ? '?' + query : ''), {trigger: true, replace: !!options.replace});
        },

        /**
         * Link (href) of a route in the current mode: '/v2/book/service/5/' (path) or '#book/service/5' (hash)
         *
         * @param {String} fragment
         * @return {String}
         */
        url: function (fragment) {
            this._init();
            var clean = this.clean(fragment);
            if (this._mode !== MODE_PATH) {
                return '#' + clean;
            }
            return this._pathUrl(clean, this._queryOf(fragment));
        },

        /**
         * Current route without query and trailing slash, e.g. 'book/service/5'
         *
         * @return {String}
         */
        routeFragment: function () {
            if (!Backbone.History.started) {
                return '';
            }
            return this.clean(Backbone.history.getFragment());
        },

        /**
         * Page path for analytics, the same in both modes: '/v2/book/service/5/' (the PWA shell reports
         * '/v2/pwa/<route>/' as a label of PWA traffic)
         *
         * @return {String}
         */
        pagePath: function () {
            this._init();
            var fragment = this.routeFragment();
            return this._root + (fragment ? fragment + '/' : '');
        },

        /**
         * Normalizes a route fragment: drops leading '#'/'/', the /v2 (or /v2/pwa) root, a query part,
         * trailing slashes and index.php
         *
         * @param {String} fragment
         * @return {String}
         */
        clean: function (fragment) {
            var value = String(fragment === null || fragment === undefined ? '' : fragment);
            var queryPos = value.indexOf('?');
            if (queryPos !== -1) {
                value = value.slice(0, queryPos);
            }
            value = value.replace(/^[#\/!]+/, '').replace(/^v2(\/pwa)?(\/+|$)/, '').replace(/^\/+/, '');
            value = value.replace(/\/+$/, '');
            if (/^index(\.php)?(\/index)?$/.test(value)) {
                value = '';
            }
            return value;
        },

        /**
         * Route fragment of an href / url, or null when it is not a page of this SPA
         * (other site, server endpoint outside the root, javascript:, mailto:, ...)
         *
         * @param {String} href
         * @return {String|null}
         */
        toFragment: function (href) {
            if (href === null || href === undefined) {
                return null;
            }
            this._init();
            var value = String(href).replace(/^\s+|\s+$/g, '');
            if (value === '') {
                return null;
            }
            if (value.charAt(0) === '#') {
                return this.clean(value);
            }
            if (/^[a-z][a-z0-9+.\-]*:/i.test(value)) {
                // absolute url: only pages of this site
                var origin = window.location.protocol + '//' + window.location.host;
                if ((value + '/').toLowerCase().indexOf(origin.toLowerCase() + '/') !== 0) {
                    return null;
                }
                value = value.slice(origin.length) || '/';
            }
            if (value.charAt(0) !== '/' || value.indexOf('//') === 0) {
                return null;
            }

            var hashPos = value.indexOf('#');
            var hash = hashPos !== -1 ? value.slice(hashPos + 1) : '';
            var path = hashPos !== -1 ? value.slice(0, hashPos) : value;
            var queryPos = path.indexOf('?');
            if (queryPos !== -1) {
                path = path.slice(0, queryPos);
            }
            var rest = this._pathBeyondRoot(path);
            if (rest === null) {
                return null;
            }
            if (hash && !TOKEN_FRAGMENT.test(hash)) {
                var hashFragment = this.clean(hash);
                if (rest === '' || this.matchesRoute(hashFragment)) {
                    return hashFragment;
                }
            }
            return this.clean(this._fromUrlPath(rest));
        },

        /**
         * Is the fragment a route of the SPA (the '*notFound' catch-all does not count)?
         *
         * @param {String} fragment
         * @return {Boolean}
         */
        matchesRoute: function (fragment) {
            if (fragment === null || fragment === undefined || TOKEN_FRAGMENT.test(fragment)) {
                return false;
            }
            var value = String(fragment);
            var catchAll = this._getCatchAllSources();
            return _.some(Backbone.history.handlers, function (handler) {
                return !_.contains(catchAll, handler.route.source) && handler.route.test(value);
            });
        },

        /**
         * Rewrites href="#route" links of a rendered html string to path urls (path mode only)
         *
         * @param {String} html
         * @return {String}
         */
        rewriteHtml: function (html) {
            var _this = this;
            if (!this._started || this._mode !== MODE_PATH || !_.isString(html) || html.indexOf('#') === -1) {
                return html;
            }
            return html.replace(/<a\b[^>]*>/gi, function (tag) {
                if (/\sdata-(bs-)?toggle\s*=|\sdata-bypass\b|\srole\s*=\s*["']?tab\b/i.test(tag)) {
                    return tag;
                }
                var marker = '';
                tag = tag.replace(/(\shref\s*=\s*)(["'])#([^"']*)\2/i, function (match, prefix, quote, value) {
                    var unescaped = _this._unescapeHtml(value);
                    var url = _this._routeHref(unescaped);
                    if (url === null) {
                        return match;
                    }
                    marker = ' data-sb-route="1"';
                    if (_this._isCrawlTrap(_this.clean(unescaped)) && !/\srel\s*=/i.test(tag)) {
                        marker += ' rel="nofollow"';
                    }
                    return prefix + quote + _this._escapeAttr(url) + quote;
                });
                return marker ? tag.replace(/^<a\b/i, '<a' + marker) : tag;
            });
        },

        /**
         * Rewrites href="#route" links inside an element (default: document) to path urls (path mode only)
         *
         * @param {Element|jQuery} [el]
         */
        rewriteLinks: function (el) {
            var _this = this;
            if (!this._started || this._mode !== MODE_PATH) {
                return;
            }
            $(el || document).find('a[href^="#"]')
                .not('[data-toggle],[data-bs-toggle],[data-bypass],[role="tab"]')
                .each(function () {
                    var value = $(this).attr('href').slice(1);
                    var url = _this._routeHref(value);
                    if (url !== null) {
                        $(this).attr('href', url).attr('data-sb-route', '1');
                        if (_this._isCrawlTrap(_this.clean(value)) && !$(this).attr('rel')) {
                            $(this).attr('rel', 'nofollow');
                        }
                    }
                });
        },

        // ----- internals -----

        _init: function () {
            if (this._mode) {
                return;
            }
            var config = Config.getInstance();
            var root = config.getOption('spa_root') || ((config.getOption('base_url') || '/v2') + '/');
            this._root = ('/' + root + '/').replace(/^\/+/, '/').replace(/\/+$/, '/');
            this._stickyQuery = String(config.getOption('nav_sticky_query') || '').replace(/^[?&]+/, '');
            this._stickyParams = _.isArray(config.getOption('nav_sticky_params')) ? config.getOption('nav_sticky_params') : null;
            this._mode = this._detectMode(config.getOption('nav_mode'));
        },

        _detectMode: function (serverMode) {
            if (serverMode !== MODE_PATH) {
                return MODE_HASH;
            }
            try {
                if (window.self !== window.top) {
                    return MODE_HASH; // embedded: the url is not visible, the query carries widget settings
                }
            } catch (e) {
                return MODE_HASH;
            }
            if (this._pathBeyondRoot(window.location.pathname) === null) {
                return MODE_HASH;
            }
            if (TOKEN_FRAGMENT.test(this._rawHash())) {
                return MODE_HASH;
            }
            if (!window.history || !window.history.pushState) {
                return MODE_HASH;
            }
            try {
                window.history.replaceState(window.history.state, document.title, window.location.href);
            } catch (e) {
                return MODE_HASH; // e.g. sandboxed iframe
            }
            return MODE_PATH;
        },

        _startHash: function (history) {
            // a path url (sent in path mode, or a path link opened inside a widget) - move the route into the hash
            var rest = this._pathBeyondRoot(window.location.pathname);
            var pathFragment = rest ? this.clean(this._fromUrlPath(rest)) : '';
            var hash = this._rawHash();
            if (pathFragment && !TOKEN_FRAGMENT.test(hash) && this.matchesRoute(pathFragment)) {
                var target = hash && this.matchesRoute(this.clean(hash)) ? hash.replace(/^[#\/]+/, '') : pathFragment;
                this._replaceUrl(this._root + window.location.search + '#' + target, true);
            }
            return history.start();
        },

        _startPath: function (history) {
            var _this = this;

            // legacy hash url: the hash wins when it is a route
            var rawHash = this._rawHash();
            var hashFragment = this.clean(rawHash);
            if (rawHash && hashFragment !== '' && this.matchesRoute(hashFragment)) {
                hashFragment = this._canonicalRoute(hashFragment);
                var search = this._mergeQuery(window.location.search.replace(/^\?/, ''), this._queryOf(rawHash));
                this._replaceUrl(this._root + this._encodePath(hashFragment) + '/' + (search ? '?' + search : ''));
            } else if (rawHash === '' && /#$/.test(window.location.href)) {
                this._replaceUrl(window.location.pathname + window.location.search);
            }
            if (this._stickyParams) {
                // the landing query may also come from inside a legacy hash (/v2/#book?theme=default)
                this._stickyQuery = this._mergeQuery(
                    this._stickyQuery, this._filterQuery(window.location.search, this._stickyParams)
                );
            }
            this._addPrintStyle();

            history.getPath = function () {
                var rest = _this._pathBeyondRoot(this.decodeFragment(this.location.pathname));
                return _this.clean(_this._fromUrlPath(rest === null ? '' : rest));
            };

            history.navigate = function (fragment, options) {
                if (!Backbone.History.started) {
                    return false;
                }
                if (!options || options === true) {
                    options = {trigger: !!options};
                }
                var target = _this.toFragment(fragment);
                if (target === null) {
                    target = _this.clean(fragment);
                }
                target = _this._canonicalRoute(target);
                var decoded = this.decodeFragment(target);
                var query = _this._queryOf(fragment);
                if (this.fragment === decoded) {
                    if (query) {
                        _this._replaceUrl(_this._pathUrl(target, query)); // same route, new query
                    } else if (/#/.test(window.location.href)) {
                        // same route opened through a '#route' link - drop the hash from the address bar
                        _this._replaceUrl(window.location.pathname + window.location.search);
                    }
                    return;
                }
                this.fragment = decoded;
                this.history[options.replace ? 'replaceState' : 'pushState'](
                    {}, document.title, _this._pathUrl(target, query)
                );
                _this._afterPush();
                if (options.trigger) {
                    return this.loadUrl(decoded);
                }
            };

            this._installRenderer();
            $(window).on('hashchange.sbnav', function () {
                var raw = _this._rawHash();
                if (!raw || TOKEN_FRAGMENT.test(raw)) {
                    return;
                }
                var fragment = _this.clean(raw);
                if (fragment !== '' && _this.matchesRoute(fragment)) {
                    Backbone.history.navigate(fragment, {trigger: true, replace: true});
                }
            });

            var result = history.start({pushState: true, hashChange: false, root: this._root});
            this.rewriteLinks(document);
            return result;
        },

        // In hash navigation every route change fired popstate; theme code relies on it (modal backdrop cleanup)
        _afterPush: function () {
            if (_.isFunction(window.onpopstate)) {
                try {
                    window.onpopstate({state: null, sbNavigation: true});
                } catch (e) {
                }
            }
        },

        _installRenderer: function () {
            var _this = this;
            var render = Marionette.Renderer.render;
            Marionette.Renderer.render = function () {
                return _this.rewriteHtml(render.apply(this, arguments));
            };
        },

        _bindClicks: function () {
            var _this = this;
            $(document).on('click.sbnav', 'a[href]', function (e) {
                if (e.isDefaultPrevented() || e.isTrigger) {
                    return;
                }
                if (e.which > 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                    return;
                }
                var $link = $(this);
                var target = $link.attr('target');
                if (target && target !== '_self') {
                    return;
                }
                if ($link.is('[download],[data-toggle],[data-bs-toggle],[data-bypass],[rel~="external"]')) {
                    return;
                }
                var href = $link.attr('href');
                if (href.charAt(0) === '#') {
                    if (_this._mode !== MODE_PATH) {
                        return; // the browser changes the hash, Backbone routes it
                    }
                    if (href === '#') {
                        // in hash navigation an unhandled '#' link led to the index page
                        e.preventDefault();
                        Backbone.history.navigate('', {trigger: true});
                        return;
                    }
                }
                var fragment = _this.toFragment(href);
                if (fragment === null || !_this.matchesRoute(fragment)) {
                    return;
                }
                var query = _this._queryOf(href);
                if (href.charAt(0) !== '#' && _this._needsServer(query)) {
                    return; // the server must see this query (e.g. /v2/book?service=5, ?ada=1): full page load
                }
                e.preventDefault();
                Backbone.history.navigate(fragment + (query ? '?' + query : ''), {trigger: true});
            });
        },

        // listeners registered by inline scripts of the layout (e.g. the page view tracker) and GTM
        _bindRouteListeners: function () {
            var _this = this;
            var notify = _.debounce(function () {
                var fragment = _this.routeFragment();
                _.each(window.sbRouteListeners || [], function (listener) {
                    try {
                        listener(fragment, _this.pagePath());
                    } catch (e) {
                    }
                });
                if (window.dataLayer && _.isFunction(window.dataLayer.push)) {
                    window.dataLayer.push({
                        event: 'sb_route_change',
                        sb_route: fragment,
                        sb_page_path: _this.pagePath()
                    });
                }
            }, 50);
            Backbone.history.on('route', function () {
                if (!_this._booting) { // the landing page is reported by the layout scripts themselves
                    notify();
                }
            });
        },

        _patchDecode: function (history) {
            var decode = Backbone.History.prototype.decodeFragment;
            history.decodeFragment = function (fragment) {
                try {
                    return decode.call(this, fragment);
                } catch (e) {
                    return fragment; // malformed '%' must not break the page
                }
            };
        },

        _routeHref: function (value) {
            if (value === '' || TOKEN_FRAGMENT.test(value)) {
                return null;
            }
            var fragment = this.clean(value);
            if (fragment === '' || !this.matchesRoute(fragment)) {
                return null;
            }
            return this._pathUrl(fragment, this._queryOf(value));
        },

        // Single-segment fragments that match the booking route only through its '(:someTrash)' tail ('#booking',
        // '#book-now') would become paths the server does not serve as the SPA (/v2/booking/ is a REST controller)
        _canonicalRoute: function (fragment) {
            var value = String(fragment);
            if (value.indexOf('/') === -1 && /^book./.test(value) && value !== 'book-service' && value !== 'book-class') {
                return value.indexOf('book-service') === 0 ? 'book-service' : 'book';
            }
            return value;
        },

        // booking step links of concrete dates/times form an endless url space for crawlers
        _isCrawlTrap: function (fragment) {
            return /^book(-service|-class)?\//.test(fragment) && /(^|\/)(date|time)\//.test(fragment);
        },

        // a link query other than the current sticky one changes what the server renders
        _needsServer: function (query) {
            return !!query && !this._sameQuery(query, this._stickyQuery);
        },

        _sameQuery: function (a, b) {
            var normalize = function (query) {
                return _.compact(String(query || '').replace(/^[?&]+/, '').split('&')).sort().join('&');
            };
            return normalize(a) === normalize(b);
        },

        _filterQuery: function (search, keys) {
            return _.filter(String(search || '').replace(/^[?&]+/, '').split('&'), function (pair) {
                var key = pair.split('=')[0].replace(/\+/g, ' ');
                try {
                    key = decodeURIComponent(key);
                } catch (e) {
                    // malformed '%' - compare the raw key
                }
                return pair && _.contains(keys, key);
            }).join('&');
        },

        // Bootstrap prints the href after every link not starting with '#'; route links don't need that
        _addPrintStyle: function () {
            try {
                var style = document.createElement('style');
                style.appendChild(document.createTextNode('@media print{a[data-sb-route]:after{content:none !important}}'));
                document.getElementsByTagName('head')[0].appendChild(style);
            } catch (e) {
            }
        },

        _pathUrl: function (fragment, extraQuery) {
            fragment = this._canonicalRoute(fragment);
            var query = this._mergeQuery(this._stickyQuery, extraQuery);
            return this._root + (fragment ? this._encodePath(fragment) + '/' : '') + (query ? '?' + query : '');
        },

        // characters not allowed in a path are encoded; an encoded slash becomes '~2F' (Apache rejects %2F)
        _encodePath: function (fragment) {
            return String(fragment)
                .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[^A-Za-z0-9\-._~!$&()*+,;=:@\/%]/g, function (chars) {
                    return chars === '\'' ? '%27' : encodeURIComponent(chars);
                })
                .replace(/%2F/gi, '~2F');
        },

        _fromUrlPath: function (path) {
            return String(path).replace(/~2F/gi, '%2F');
        },

        // path after the root ('' for the root itself), or null when the path is outside the root
        _pathBeyondRoot: function (path) {
            var value = String(path || '/');
            var withSlash = value.replace(/[^\/]$/, '$&/');
            var roots = this._root === MAIN_ROOT ? [this._root] : [this._root, MAIN_ROOT];
            for (var i = 0; i < roots.length; i++) {
                if (withSlash.indexOf(roots[i]) === 0) {
                    return value.slice(roots[i].length);
                }
            }
            return null;
        },

        _rawHash: function () {
            var match = window.location.href.match(/#(.*)$/);
            return match ? match[1] : '';
        },

        // query part of a fragment ('book?x=1', '#book?x=1') or of a url ('/v2/book/?x=1#y')
        _queryOf: function (value) {
            var text = String(value === null || value === undefined ? '' : value);
            if (text.charAt(0) === '#') {
                text = text.slice(1);
            } else if (this._isUrl(text) && text.indexOf('#') !== -1) {
                text = text.slice(0, text.indexOf('#'));
            }
            var pos = text.indexOf('?');
            return pos === -1 ? '' : text.slice(pos + 1);
        },

        // merges query strings, later values override earlier ones with the same key
        _mergeQuery: function () {
            var keys = [];
            var values = {};
            _.each(arguments, function (query) {
                _.each(String(query || '').replace(/^[?&]+/, '').split('&'), function (pair) {
                    if (!pair) {
                        return;
                    }
                    var key = pair.split('=')[0];
                    if (!_.has(values, key)) {
                        keys.push(key);
                    }
                    values[key] = pair;
                });
            });
            return _.map(keys, function (key) {
                return values[key];
            }).join('&');
        },

        _replaceUrl: function (url, allowReload) {
            try {
                window.history.replaceState(window.history.state, document.title, url);
            } catch (e) {
                if (allowReload) {
                    window.location.replace(url);
                }
            }
        },

        _isUrl: function (value) {
            return /^[a-z][a-z0-9+.\-]*:/i.test(value) || value.indexOf('//') === 0 || value.charAt(0) === '/';
        },

        _getCatchAllSources: function () {
            if (!this._catchAllSources) {
                this._catchAllSources = _.map(CATCH_ALL_ROUTES, function (route) {
                    return Backbone.Router.prototype._routeToRegExp(route).source;
                });
            }
            return this._catchAllSources;
        },

        _unescapeHtml: function (value) {
            return String(value)
                .replace(/&#x3D;|&#61;/gi, '=')
                .replace(/&#x27;|&#39;/gi, '\'')
                .replace(/&#x60;|&#96;/gi, '`')
                .replace(/&quot;/gi, '"')
                .replace(/&lt;/gi, '<')
                .replace(/&gt;/gi, '>')
                .replace(/&amp;/gi, '&');
        },

        _escapeAttr: function (value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        }
    };

    return Navigation;
});

